import { deviceTimeZone, localDateString, type MealSlot } from '@gramas/shared'
import { Button, Card, Chip, MacroPreview, NumberField, Sheet } from '@gramas/ui'
import { useEffect, useRef, useState } from 'react'
import { api } from '../../api/client.js'
import { logAdHoc, slotFor } from '../../db/repo.js'
import { PersonaSpinner } from '../../glitime/PersonaSpinner.js'
import { logContext } from '../../sass/context.js'
import { sassLine } from '../../sass/engine.js'
import { maybeLogToast } from '../../sass/toast.js'
import { syncNow } from '../../sync/engine.js'
import { SlotPicker } from './SlotPicker.js'

export interface DishEstimate {
  name: string
  portionG: number
  kcal: number
  kcalLow: number
  kcalHigh: number
  proteinG: number
  carbsG: number
  fatG: number
  confidence: number
  assumptions: string[]
  hadPhoto: boolean
}

type Phase =
  | { name: 'input' }
  | { name: 'working' }
  | { name: 'review'; estimate: DishEstimate }
  | { name: 'failed'; message: string }

/** Three bands, because a percentage implies a precision an estimate cannot have. */
function confidenceLabel(value: number): { text: string; tone: 'accent' | 'warn' | 'neutral' } {
  if (value >= 0.7) return { text: 'Gana tikslu', tone: 'accent' }
  if (value >= 0.45) return { text: 'Apytiksliai', tone: 'neutral' }
  return { text: 'Labai apytiksliai', tone: 'warn' }
}

/**
 * A plate in a restaurant, estimated.
 *
 * No Lithuanian restaurant publishes nutrition, so the alternative to an
 * estimate is not a better number — it is an unlogged meal, and a day with a
 * hole in it is worse than a day with a wide error bar.
 *
 * What makes that honest is showing the working: a range rather than a figure,
 * a plain-language confidence rather than a percentage, and the two or three
 * assumptions that actually moved the number, so a wrong one can be spotted and
 * the amount corrected. A photograph roughly halves the range, which is why the
 * sheet asks for one even though it does not require it.
 */
export function DishEstimateSheet({
  open,
  onOpenChange,
  date,
  initialDescription = '',
  onLogged,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  date?: string
  /** Prefilled from the search box, since that is where you gave up looking. */
  initialDescription?: string
  onLogged?: () => void
}) {
  const [description, setDescription] = useState(initialDescription)
  const [photo, setPhoto] = useState<File | null>(null)
  const [phase, setPhase] = useState<Phase>({ name: 'input' })
  const [portionG, setPortionG] = useState<number | null>(null)
  const [slot, setSlot] = useState<MealSlot>(() => slotFor(null, 'g'))
  const [saving, setSaving] = useState(false)
  const fileInput = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (open) {
      setDescription(initialDescription)
      setPhoto(null)
      setPhase({ name: 'input' })
      setPortionG(null)
      setSlot(slotFor(null, 'g'))
    }
  }, [open, initialDescription])

  const estimate = phase.name === 'review' ? phase.estimate : null
  // Everything scales with the portion, because the portion is the one number
  // the person at the table can actually correct.
  const scale = estimate && portionG ? portionG / estimate.portionG : 1

  const run = async () => {
    if (description.trim() === '' && !photo) return
    setPhase({ name: 'working' })
    try {
      let res: { estimate: DishEstimate }
      if (photo) {
        const form = new FormData()
        form.append('image', photo, 'dish.jpg')
        if (description.trim() !== '') form.append('description', description.trim())
        res = await api.post<{ estimate: DishEstimate }>('/api/ai/estimate-dish', form)
      } else {
        res = await api.post<{ estimate: DishEstimate }>('/api/ai/estimate-dish', {
          description: description.trim(),
        })
      }
      setPortionG(res.estimate.portionG)
      setSlot(slotFor(null, 'g', res.estimate.name))
      setPhase({ name: 'review', estimate: res.estimate })
    } catch (error) {
      setPhase({
        name: 'failed',
        message:
          (error as { message?: string }).message ??
          sassLine('error_save', {}) ??
          'Nepavyko įvertinti.',
      })
    }
  }

  const commit = async () => {
    if (!estimate || !portionG || saving) return
    setSaving(true)
    try {
      const per100g = {
        kcal: (estimate.kcal / estimate.portionG) * 100,
        protein: (estimate.proteinG / estimate.portionG) * 100,
        carbs: (estimate.carbsG / estimate.portionG) * 100,
        fat: (estimate.fatG / estimate.portionG) * 100,
        sugars: null,
        satFat: null,
        fiber: null,
        salt: null,
      }
      await logAdHoc(
        estimate.name,
        {
          kcal: (per100g.kcal * portionG) / 100,
          protein: (per100g.protein * portionG) / 100,
          carbs: (per100g.carbs * portionG) / 100,
          fat: (per100g.fat * portionG) / 100,
          sugars: null,
          satFat: null,
          fiber: null,
          salt: null,
        },
        {
          slot,
          quantityG: portionG,
          servings: 1,
          logDate: date ?? localDateString(new Date(), deviceTimeZone()),
          // The diary keeps the fact that this was a guess. Three months later
          // that is the difference between a number and a number you can trust
          // — which is why the range is scaled with the portion like everything
          // else here, instead of describing the portion the model assumed.
          notes: `Įvertinta${estimate.hadPhoto ? ' iš nuotraukos' : ''} · ${Math.round(
            (estimate.kcalLow * portionG) / estimate.portionG,
          )}–${Math.round((estimate.kcalHigh * portionG) / estimate.portionG)} kcal`,
        },
      )
      void syncNow()
      onOpenChange(false)
      onLogged?.()
      const logDate = date ?? localDateString(new Date(), deviceTimeZone())
      maybeLogToast(
        await logContext(
          logDate,
          slot,
          Math.round((estimate.kcal / estimate.portionG) * portionG),
          estimate.name,
        ),
      )
    } finally {
      setSaving(false)
    }
  }

  const confidence = estimate ? confidenceLabel(estimate.confidence) : null

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
      title="Įvertinti patiekalą"
      footer={
        estimate ? (
          <div className="flex gap-2">
            <Button block onClick={() => setPhase({ name: 'input' })} disabled={saving}>
              Iš naujo
            </Button>
            <Button
              variant="primary"
              block
              onClick={() => void commit()}
              disabled={saving || !portionG}
            >
              Įrašyti {Math.round(estimate.kcal * scale)} kcal
            </Button>
          </div>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-4 px-4 py-4">
        {phase.name === 'working' && (
          <div className="flex flex-col items-center gap-3 py-10">
            <PersonaSpinner surface="photo" />
            <p className="text-sm text-mute">Vertinama…</p>
          </div>
        )}

        {(phase.name === 'input' || phase.name === 'failed') && (
          <>
            <p className="text-sm text-mute">
              Restoranai maistinės vertės neskelbia. Aprašykite patiekalą, o dar geriau —
              nufotografuokite lėkštę: iš nuotraukos matyti porcija, o iš žodžių — kas padaže.
            </p>

            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="pvz. kepta lašiša su bulvių koše ir grietinėlės padažu"
              className="w-full resize-none rounded-[var(--radius-control)] border border-line bg-bg px-3 py-2 text-base text-ink placeholder:text-mute"
            />

            <input
              ref={fileInput}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0] ?? null
                e.target.value = ''
                setPhoto(file)
              }}
            />
            <Button block onClick={() => fileInput.current?.click()}>
              {photo ? `Nuotrauka pridėta — pakeisti` : 'Nufotografuoti lėkštę'}
            </Button>

            <Button
              variant="primary"
              block
              disabled={description.trim() === '' && !photo}
              onClick={() => void run()}
            >
              Įvertinti
            </Button>

            {phase.name === 'failed' && <p className="text-sm text-danger">{phase.message}</p>}
          </>
        )}

        {estimate && (
          <>
            <div className="flex items-center justify-between gap-2">
              <span className="text-base text-ink">{estimate.name}</span>
              {confidence && <Chip tone={confidence.tone}>{confidence.text}</Chip>}
            </div>

            <Card className="px-3 py-3">
              <div className="flex items-baseline gap-2">
                <span className="tnum font-display text-3xl text-ink">
                  {Math.round(estimate.kcal * scale)}
                </span>
                <span className="text-sm text-mute">kcal</span>
              </div>
              {/* The range is the honest part; the single figure above it is a
                  convenience, not a measurement. */}
              <p className="tnum mt-1 text-sm text-mute">
                Tikėtina tarp {Math.round(estimate.kcalLow * scale)} ir{' '}
                {Math.round(estimate.kcalHigh * scale)} kcal
              </p>
              <MacroPreview
                frame="plain"
                className="mt-3 border-t border-line pt-3"
                proteinG={estimate.proteinG * scale}
                carbsG={estimate.carbsG * scale}
                fatG={estimate.fatG * scale}
              />
            </Card>

            {estimate.assumptions.length > 0 && (
              <div className="flex flex-col gap-1">
                <span className="text-xs text-mute">Kuo remtasi</span>
                <ul className="flex flex-col gap-0.5">
                  {estimate.assumptions.map((line) => (
                    <li key={line} className="text-sm text-dim">
                      · {line}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <NumberField
              label="Porcijos svoris"
              suffix="g"
              value={portionG}
              onChange={setPortionG}
            />
            <p className="text-xs text-mute">
              Jei porcija atrodė didesnė ar mažesnė — pakeiskite čia, visa kita persiskaičiuos.
            </p>

            <SlotPicker value={slot} onChange={setSlot} />
          </>
        )}
      </div>
    </Sheet>
  )
}
